import { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { FileText, Camera, User, Download, Mail, ExternalLink } from "lucide-react";

const NAVY_PORTRAIT = "https://customer-assets.emergentagent.com/job_morse-chronicles/artifacts/tfncxvd5_Don%20Inbody%20Navy.jpg";
const SCHOLAR_URL = "https://scholar.google.com/citations?user=WypJ78AAAAAJ&hl=en&oi=sra";

const SHORT_BIO = `Captain Donald S. Inbody, USN (Ret), PhD, is a retired Senior Lecturer in Political Science at Texas State University and a retired U.S. Navy Captain with 28 years of active-duty service. A specialist in UOCAVA and voting rights, he is the author of The Soldier Vote: War, Politics, and the Ballot in America. Dr. Inbody has testified before the Presidential Commission on Election Administration and remains a frequent contributor to the Overseas Vote Foundation. He holds a PhD in Government from the University of Texas at Austin.`;

const LONG_BIO = `Donald S. Inbody is a retired Senior Lecturer in Political Science at Texas State University, where he maintains an ongoing professional affiliation. A leading expert on military and overseas voting, Dr. Inbody served 28 years on active duty in the U.S. Navy, retiring at the rank of Captain. Having lived and served across the globe, he brings a unique, firsthand perspective to the logistical and systemic barriers faced by civilian and military citizens stationed beyond U.S. borders.

Dr. Inbody is a long-time supporter of the U.S. Vote Foundation and the Overseas Vote Foundation (OVF), contributing regularly to the OVF Research Letter and participating in its annual Summits. In 2013, he provided expert testimony before the Presidential Commission on Election Administration regarding the challenges of the Uniformed and Overseas Citizens Absentee Voting Act (UOCAVA).

An accomplished scholar, Dr. Inbody's work has appeared in the Election Law Journal. He is the author of The Soldier Vote: War, Politics, and the Ballot in America, the definitive historical and statistical analysis of military absentee voting in the United States. He holds a PhD in Government from the University of Texas at Austin.

Inbody is also a lifelong musician. He holds a Bachelor's and Master's Degree in Music Education from Wichita State University, taught K-12 Vocal Music in Kansas, and has directed church choirs throughout his life. In 2004 he co-founded BuffaloGrass, an Americana roots band drawing on the sounds of the Great Plains.

Wake of the Falcon, the first novel in his Morse Chronicles series, is currently in progress. Set in August 1914, it follows Captain Hank Morse aboard the aging armored cruiser USS Wichita through the Dutch East Indies — a story rooted in Inbody's firsthand knowledge of naval command at sea.`;

export default function Press() {
  const [activeBio, setActiveBio] = useState("short");

  return (
    <div className="bg-[#F5F0E8]">
      <SEO
        title="Press & Media — Donald S. Inbody"
        description="Press kit, biography, and media resources for Donald S. Inbody — author, retired U.S. Navy Captain, and political scientist."
        canonical="https://inbody.net/press"
        keywords="Donald Inbody press kit, biography, media inquiries, author photo"
      />
      <Navbar />

      {/* Header */}
      <section className="relative pt-32 pb-20 bg-[#0A1628] overflow-hidden">
        <div className="absolute inset-0 opacity-5"
          style={{ backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 40px, rgba(197,160,89,0.3) 40px, rgba(197,160,89,0.3) 41px), repeating-linear-gradient(90deg, transparent, transparent 40px, rgba(197,160,89,0.3) 40px, rgba(197,160,89,0.3) 41px)" }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <p className="overline text-[#C5A059] mb-3">Media Resources</p>
          <h1 data-testid="press-page-title" className="font-serif text-5xl font-black text-[#F5F0E8] mb-4">
            Press &amp; Media
          </h1>
          <p className="text-[#F5F0E8]/60 max-w-xl leading-relaxed">
            Biography, photo, and curriculum vitae for media, event organisers, and academic institutions.
            For interview requests and speaking engagements, please use the contact form below.
          </p>
        </div>
      </section>

      {/* ── Photo + Bio ── */}
      <section className="py-24 bg-[#F5F0E8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 items-start">

            {/* Photo column */}
            <div className="space-y-4">
              <p className="overline mb-3">
                <Camera size={14} className="inline mr-1" /> Professional Photo
              </p>
              <div className="relative group overflow-hidden shadow-xl">
                <img
                  src={NAVY_PORTRAIT}
                  alt="Donald S. Inbody — Captain, U.S. Navy (Retired)"
                  data-testid="press-photo"
                  className="w-full h-auto block"
                />
                <div className="absolute inset-0 bg-[#0A1628]/0 group-hover:bg-[#0A1628]/10 transition-colors duration-200" />
              </div>
              <p className="text-xs text-[#2D3142]/50 leading-relaxed">
                High-resolution photo available on request.
                Please credit: <em>Donald S. Inbody</em>.
              </p>
              <a
                href="mailto:author@inbody.net?subject=Press Photo Request"
                data-testid="press-photo-request"
                className="inline-flex items-center gap-2 border border-[#0A1628] text-[#0A1628] px-4 py-2 text-xs font-semibold hover:bg-[#0A1628] hover:text-[#F5F0E8] transition-colors"
              >
                <Mail size={12} /> Request Hi-Res Photo
              </a>
            </div>

            {/* Bio column */}
            <div className="lg:col-span-2">
              {/* Name / Rank heading */}
              <div className="mb-6">
                <h2 className="font-serif text-2xl font-bold text-[#0A1628]">
                  Captain Donald S. Inbody, USN (Ret), PhD
                </h2>
                <p className="text-[#C5A059] text-sm font-medium mt-1">
                  Senior Lecturer in Political Science, Texas State University (Ret.) &middot; Author &middot; Naval Historian
                </p>
              </div>
              <div className="flex items-center justify-between mb-6">
                <p className="overline">
                  <User size={14} className="inline mr-1" /> Biography
                </p>
                <div className="flex gap-1">
                  {["short", "long"].map(t => (
                    <button
                      key={t}
                      data-testid={`bio-toggle-${t}`}
                      onClick={() => setActiveBio(t)}
                      className={`px-4 py-1.5 text-xs font-semibold uppercase tracking-widest transition-colors ${
                        activeBio === t ? "bg-[#0A1628] text-[#F5F0E8]" : "border border-[#0A1628]/20 text-[#2D3142] hover:border-[#0A1628]"
                      }`}
                    >
                      {t === "short" ? "Short" : "Full"}
                    </button>
                  ))}
                </div>
              </div>

              {activeBio === "short" ? (
                <div data-testid="bio-short">
                  <p className="text-[#2D3142] leading-relaxed text-lg">{SHORT_BIO}</p>
                  <button
                    onClick={() => navigator.clipboard.writeText(SHORT_BIO)}
                    className="mt-4 inline-flex items-center gap-2 text-xs text-[#2D3142]/50 hover:text-[#0A1628] transition-colors"
                  >
                    <FileText size={12} /> Copy to clipboard
                  </button>
                </div>
              ) : (
                <div data-testid="bio-long" className="space-y-4">
                  {LONG_BIO.split("\n\n").map((para, i) => (
                    <p key={i} className="text-[#2D3142] leading-relaxed">{para}</p>
                  ))}
                  <button
                    onClick={() => navigator.clipboard.writeText(LONG_BIO)}
                    className="mt-2 inline-flex items-center gap-2 text-xs text-[#2D3142]/50 hover:text-[#0A1628] transition-colors"
                  >
                    <FileText size={12} /> Copy to clipboard
                  </button>
                </div>
              )}

              {/* Key facts */}
              <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-4">
                {[
                  { label: "Rank", value: "Captain, U.S. Navy (Ret.)" },
                  { label: "Institution", value: "Texas State University" },
                  { label: "Degree", value: "PhD, Government, UT Austin" },
                  { label: "Publisher", value: "Palgrave Macmillan" },
                  { label: "Specialty", value: "UOCAVA & Military Voting" },
                  { label: "Location", value: "Central Texas" },
                ].map(({ label, value }) => (
                  <div key={label} className="border border-[#0A1628]/12 p-4 bg-white">
                    <div className="text-xs text-[#2D3142]/50 uppercase tracking-widest mb-1">{label}</div>
                    <div className="text-sm font-semibold text-[#0A1628]">{value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CV ── */}
      <section className="py-16 bg-white" data-testid="press-cv-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-10">
            <div className="h-px flex-1 bg-[#0A1628]/15" />
            <p className="overline">Curriculum Vitae</p>
            <div className="h-px flex-1 bg-[#0A1628]/15" />
          </div>
          <div className="max-w-2xl mx-auto text-center">
            <div className="border-2 border-dashed border-[#0A1628]/15 p-16 bg-[#F5F0E8]/50 mb-6">
              <FileText size={40} className="text-[#C5A059] mx-auto mb-4" />
              <h3 className="font-serif text-xl font-bold text-[#0A1628] mb-3">CV Coming Soon</h3>
              <p className="text-[#2D3142]/60 text-sm leading-relaxed mb-6">
                A full curriculum vitae will be available for download here. In the meantime,
                please contact directly for a copy.
              </p>
              <a
                href="/contact"
                className="inline-flex items-center gap-2 bg-[#0A1628] text-[#F5F0E8] px-5 py-2.5 text-sm font-semibold hover:bg-[#2D3142] transition-colors"
              >
                <Mail size={14} /> Request CV
              </a>
            </div>
            <a
              href={SCHOLAR_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="press-scholar-link"
              className="inline-flex items-center gap-2 border border-[#0A1628]/20 text-[#2D3142] px-5 py-2.5 text-sm font-semibold hover:border-[#0A1628] transition-colors"
            >
              <ExternalLink size={14} /> View Publications on Google Scholar
            </a>
          </div>
        </div>
      </section>

      {/* ── Media Contact ── */}
      <section className="py-16 bg-[#0A1628]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <p className="overline text-[#C5A059] mb-3">Get in Touch</p>
          <h2 className="font-serif text-3xl font-bold text-[#F5F0E8] mb-4">Media Inquiries</h2>
          <p className="text-[#F5F0E8]/60 max-w-lg mx-auto mb-8 leading-relaxed">
            For interview requests, speaking engagements, book reviews, and academic collaboration,
            please use the contact form.
          </p>
          <a
            href="/contact"
            data-testid="press-contact-cta"
            className="inline-flex items-center gap-2 bg-[#C5A059] text-[#0A1628] px-6 py-3 text-sm font-bold hover:bg-[#C5A059]/90 transition-colors"
          >
            <Mail size={14} /> Send a Media Inquiry
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
